<script setup lang="ts">
/**
 * CV analytics page body: the dashboard screenshot in a content block (light
 * and dark captures swapped with CSS, so no flash on theme change), two
 * service cards and one button. The page title band comes from
 * MarketingFeaturePage.
 */
const { t } = useI18n()
const { isAuthenticated } = useAuthUser()

const metrics = [
  { id: 'totalViews', icon: 'bi-eye' },
  { id: 'countries', icon: 'bi-globe2' },
  { id: 'avgTime', icon: 'bi-clock' },
] as const

const features = [
  { id: 'tracking', icon: 'bi-eye', points: ['views', 'countries', 'timeSpent'] },
  { id: 'insights', icon: 'bi-graph-up-arrow', points: ['engagement', 'trends', 'optimization'] },
] as const
</script>

<template>
  <section class="sofax relative overflow-hidden bg-white dark:bg-gray-950">
    <div class="section-space">
      <div class="shell relative z-10">
        <SofaxSectionHeading
          :eyebrow="t('homepage.analytics.premiumFeature')"
          :title="t('homepage.analytics.title')"
          :text="t('homepage.analytics.subtitle')"
        />

        <div class="grid grid-cols-1 gap-y-20 lg:gap-y-[6.25rem]">
          <!-- Content block: framed dashboard screenshot, metric list on the right -->
          <div class="grid grid-cols-1 items-center gap-10 md:gap-[3.75rem] lg:grid-cols-2 xl:gap-20">
            <div class="img-shadow mx-auto w-full max-w-[31.25rem]">
              <div class="overflow-hidden rounded-[0.625rem] border border-ink bg-white dark:border-gray-600 dark:bg-gray-800">
                <NuxtImg
                  src="/analytics_white.png"
                  :alt="t('homepage.analytics.dashboard.title')"
                  format="webp"
                  quality="80"
                  loading="lazy"
                  class="w-full dark:hidden"
                />
                <NuxtImg
                  src="/analytics_dark.png"
                  :alt="t('homepage.analytics.dashboard.title')"
                  format="webp"
                  quality="80"
                  loading="lazy"
                  class="hidden w-full dark:block"
                />
              </div>
            </div>

            <div>
              <div class="mb-[3.125rem]">
                <h2 class="!text-3xl md:!text-[2.5rem]">{{ t('homepage.analytics.dashboard.title') }}</h2>
                <p class="mt-5">{{ t('homepage.analytics.dashboard.description') }}</p>
              </div>
              <ul class="flex max-w-[35rem] flex-col gap-y-5">
                <li v-for="metric in metrics" :key="metric.id" class="flex items-center gap-4">
                  <SofaxIconTile :icon="metric.icon" round size="sm" />
                  <div class="display-heading display-heading-4">{{ t(`homepage.analytics.dashboard.metrics.${metric.id}`) }}</div>
                </li>
              </ul>
            </div>
          </div>

          <!-- Service cards -->
          <ul class="grid grid-cols-1 gap-6 md:grid-cols-2">
            <li v-for="feature in features" :key="feature.id" class="card card-hover h-full p-[1.875rem]">
              <SofaxIconTile :icon="feature.icon" />
              <div class="display-heading display-heading-4 mb-4 mt-[1.875rem]">
                {{ t(`homepage.analytics.features.${feature.id}.title`) }}
              </div>
              <p class="mb-6 text-base">{{ t(`homepage.analytics.features.${feature.id}.description`) }}</p>
              <ul class="flex flex-col gap-3">
                <li v-for="point in feature.points" :key="point" class="flex items-start gap-3">
                  <i class="bi bi-check-circle-fill mt-0.5 shrink-0 text-xl text-accent" />
                  <span class="text-base">{{ t(`homepage.analytics.features.${feature.id}.points.${point}`) }}</span>
                </li>
              </ul>
            </li>
          </ul>

          <div class="text-center">
            <SofaxBtn
              :to="isAuthenticated ? '/admin/premium' : '/registration'"
              :label="t('homepage.analytics.cta')"
              variant="accent"
            />
          </div>
        </div>
      </div>

      <img
        src="/sofax/background-shape-2.svg"
        alt=""
        aria-hidden="true"
        width="174"
        height="171"
        class="pointer-events-none absolute right-28 top-16 -z-10 hidden max-w-[153px] opacity-70 xl:inline-block"
      >
    </div>
  </section>
</template>
